import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ExperienceLifecycleState, ExperienceSettings } from '../types'

interface ExperienceState {
  activeExperienceId: string | null
  lifecycleState: ExperienceLifecycleState
  settings: ExperienceSettings
  history: Record<
    string,
    {
      lastRun: number
      runCount: number
      skipCount: number
      lastCompleted?: number
    }
  >
  visitCount: number
  isPlaygroundOpen: boolean

  // Actions
  setActiveExperienceId: (id: string | null) => void
  setLifecycleState: (state: ExperienceLifecycleState) => void
  setMode: (mode: 'surprise' | 'off' | string) => void
  toggleFavorite: (id: string) => void
  toggleDisabled: (id: string) => void
  setPlaybackSpeed: (speed: number) => void
  recordRun: (id: string) => void
  recordSkip: (id: string) => void
  recordCompletion: (id: string) => void
  incrementVisitCount: () => void
  setPlaygroundOpen: (open: boolean) => void
  resetHistory: () => void
}

const DEFAULT_SETTINGS: ExperienceSettings = {
  mode: 'surprise',
  favorites: [],
  disabled: [],
  playbackSpeed: 1,
}

export const useExperienceStore = create<ExperienceState>()(
  persist(
    (set, get) => ({
      activeExperienceId: null,
      lifecycleState: 'idle',
      settings: DEFAULT_SETTINGS,
      history: {},
      visitCount: 0,
      isPlaygroundOpen: false,

      setActiveExperienceId: (id) => set({ activeExperienceId: id }),
      setLifecycleState: (state) => set({ lifecycleState: state }),

      setMode: (mode) =>
        set((state) => ({
          settings: { ...state.settings, mode },
        })),

      toggleFavorite: (id) =>
        set((state) => {
          const favorites = state.settings.favorites.includes(id)
            ? state.settings.favorites.filter((f) => f !== id)
            : [...state.settings.favorites, id]
          return { settings: { ...state.settings, favorites } }
        }),

      toggleDisabled: (id) =>
        set((state) => {
          const disabled = state.settings.disabled.includes(id)
            ? state.settings.disabled.filter((d) => d !== id)
            : [...state.settings.disabled, id]
          return { settings: { ...state.settings, disabled } }
        }),

      setPlaybackSpeed: (playbackSpeed) =>
        set((state) => ({
          settings: { ...state.settings, playbackSpeed },
        })),

      recordRun: (id) =>
        set((state) => {
          const now = Date.now()
          const current = state.history[id] || { lastRun: 0, runCount: 0, skipCount: 0 }
          return {
            history: {
              ...state.history,
              [id]: {
                ...current,
                lastRun: now,
                runCount: current.runCount + 1,
              },
            },
          }
        }),

      recordSkip: (id) =>
        set((state) => {
          const current = state.history[id] || { lastRun: 0, runCount: 0, skipCount: 0 }
          return {
            history: {
              ...state.history,
              [id]: {
                ...current,
                skipCount: current.skipCount + 1,
              },
            },
          }
        }),

      recordCompletion: (id) =>
        set((state) => {
          const now = Date.now()
          const current = state.history[id] || { lastRun: 0, runCount: 0, skipCount: 0 }
          return {
            history: {
              ...state.history,
              [id]: {
                ...current,
                lastCompleted: now,
              },
            },
          }
        }),

      incrementVisitCount: () =>
        set((state) => ({
          visitCount: state.visitCount + 1,
        })),

      setPlaygroundOpen: (isPlaygroundOpen) => set({ isPlaygroundOpen }),

      resetHistory: () => set({ history: {}, visitCount: 0 }),
    }),
    {
      name: 'nttd-experience-engine',
      partialize: (state) => ({
        settings: state.settings,
        history: state.history,
        visitCount: state.visitCount,
      }),
    }
  )
)
